import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, Phone, MapPin, Linkedin, Github, MessageCircle } from 'lucide-react';

const Contact: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const contactInfo = [
    {
      icon: <Mail size={24} />,
      label: "Email",
      value: "meet27dobariya@outlook.com",
      link: "mailto:meet27dobariya@outlook.com",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <Phone size={24} />,
      label: "Phone",
      value: "650-942-5655",
      link: "tel:+16509425655",
      color: "from-green-500 to-green-600"
    },
    {
      icon: <MapPin size={24} />,
      label: "Location",
      value: "San Francisco, CA",
      link: "#",
      color: "from-purple-500 to-purple-600"
    }
  ];

  const socialLinks = [
    {
      name: "LinkedIn",
      icon: <Linkedin size={24} />,
      url: "https://www.linkedin.com/in/dobariyameet/",
      color: "from-blue-600 to-blue-700",
      hoverColor: "hover:from-blue-500 hover:to-blue-600"
    },
    {
      name: "GitHub",
      icon: <Github size={24} />,
      url: "https://github.com/MeetDobariya275",
      color: "from-gray-700 to-gray-800",
      hoverColor: "hover:from-gray-600 hover:to-gray-700"
    }
  ];

  return (
    <section id="contact" className="py-20 bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-white mb-4">Get In Touch</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            I'm always open to discussing new opportunities, innovative projects, 
            or just having a great conversation about technology and innovation.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">Let's Connect</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                Whether you have a question about my work, want to discuss potential collaborations, 
                or just want to say hello, I'd love to hear from you. Feel free to reach out through 
                any of the channels below.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <motion.a
                  key={index}
                  href={info.link}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  className="flex items-center p-6 bg-gray-700 rounded-2xl hover:bg-gray-600 transition-all duration-300 border border-gray-600 hover:border-blue-500/50 group"
                  whileHover={{ scale: 1.02, y: -2 }}
                >
                  <div className={`bg-gradient-to-r ${info.color} p-3 rounded-full mr-4 group-hover:scale-110 transition-transform duration-200`}>
                    {info.icon}
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm font-medium">{info.label}</div>
                    <div className="text-white font-semibold">{info.value}</div>
                  </div>
                </motion.a>
              ))}
            </div>

            {/* Social Links */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Follow My Work</h4>
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                    className={`bg-gradient-to-r ${social.color} ${social.hoverColor} p-4 rounded-xl text-white transition-all duration-200`}
                    whileHover={{ scale: 1.1, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {social.icon}
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact Form Placeholder & Additional Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-8"
          >
            {/* Quick Response Info */}
            <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-2xl p-8 border border-blue-500/30">
              <div className="flex items-center mb-4">
                <MessageCircle className="text-blue-400 mr-3" size={28} />
                <h3 className="text-xl font-bold text-white">Quick Response</h3>
              </div>
              <p className="text-gray-300 mb-4">
                I typically respond to emails within 24 hours during weekdays. 
                For urgent matters, feel free to call or connect on LinkedIn.
              </p>
            </div>

            {/* Professional Summary */}
            <div className="bg-gray-700 rounded-2xl p-8 border border-gray-600">
              <h3 className="text-xl font-bold text-white mb-4">What I'm Looking For</h3>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start">
                  <span className="text-blue-400 mr-3 mt-1">•</span>
                  <span>Full-time software engineering opportunities</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-400 mr-3 mt-1">•</span>
                  <span>AI/ML focused projects and research</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-400 mr-3 mt-1">•</span>
                  <span>Innovative product development roles</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-400 mr-3 mt-1">•</span>
                  <span>Collaborations on open-source projects</span>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 1 }}
              className="text-center"
            >
              <motion.a
                href="mailto:meet27dobariya@outlook.com"
                className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full font-medium transition-all duration-200"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Mail size={20} className="mr-2" />
                Send Me a Message
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 